import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    CalendarIcon,
    Loader2,
    X,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import type { Task, TaskStatus, TaskPriority, CustomField } from '@/lib/schemas';
import { statusConfig, priorityConfig, getStatusLabel, getPriorityLabel } from './task-table';
import { useI18n } from '@/lib/i18n';

// ============================================
// FORM SCHEMA
// ============================================

const subtaskFormSchema = z.object({
    title: z.string().min(1, 'Subtask title is required').max(500),
    description: z.string().max(10000).default(''),
    status: z.enum(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED']),
    priority: z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH', 'URGENT']),
    dueDate: z.string().default(''),
    assigneeIds: z.array(z.string()),
});

type SubtaskFormData = z.infer<typeof subtaskFormSchema>;

// ============================================
// PROPS
// ============================================

interface SubtaskCreationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    parentTask: Task | null;
    projectMembers: Array<{ userId: string; user: { id: string; name: string; avatarUrl: string | null } }>;
    projectOwnerId?: string;
    projectOwner?: { id: string; name: string; email: string; avatarUrl: string | null };
    onSubmit: (data: SubtaskFormData & { parentId: string; customFieldValues?: Record<string, unknown> }) => Promise<void>;
    isLoading?: boolean;
    customFields?: CustomField[];
    // Which columns are visible - used to show relevant fields
    visibleColumns?: string[];
}

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// ============================================
// COMPONENT
// ============================================

export function SubtaskCreationDialog({
    open,
    onOpenChange,
    parentTask,
    projectMembers,
    projectOwnerId,
    projectOwner,
    onSubmit,
    isLoading,
    customFields = [],
    visibleColumns = ['title', 'status', 'priority', 'assignees', 'dueDate'],
}: SubtaskCreationDialogProps) {
    const { t } = useI18n();
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});

    // Combine owner with members for assignee selection
    const allMembers = useMemo(() => {
        // If projectOwner is provided, add it to the list
        if (projectOwner && projectOwnerId) {
            const ownerEntry = {
                userId: projectOwnerId,
                user: projectOwner,
            };
            const existingMemberIds = projectMembers.map(m => m.userId);
            if (!existingMemberIds.includes(projectOwnerId)) {
                return [ownerEntry, ...projectMembers];
            }
        }
        return projectMembers;
    }, [projectMembers, projectOwnerId, projectOwner]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<SubtaskFormData>({
        resolver: zodResolver(subtaskFormSchema),
        defaultValues: {
            title: '',
            description: '',
            status: 'TODO',
            priority: 'NONE',
            dueDate: '',
            assigneeIds: [],
        },
    });

    const selectedAssignees = watch('assigneeIds');
    const selectedStatus = watch('status');
    const selectedPriority = watch('priority');

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset({
                title: '',
                description: '',
                status: 'TODO',
                priority: 'NONE',
                dueDate: '',
                assigneeIds: [],
            });
            setCustomFieldValues({});
        }
    }, [open, reset]);

    const handleFormSubmit = async (data: SubtaskFormData) => {
        if (!parentTask) return;

        // Convert dueDate from date input format (YYYY-MM-DD) to ISO datetime string
        let convertedDueDate: string | null = null;
        if (data.dueDate) {
            const dateObj = new Date(data.dueDate + 'T00:00:00.000Z');
            convertedDueDate = dateObj.toISOString();
        }

        const submitData = {
            ...data,
            dueDate: convertedDueDate,
            parentId: parentTask.id,
            customFieldValues: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
        };
        await onSubmit(submitData as any);
    };

    const toggleAssignee = (userId: string) => {
        const current = selectedAssignees || [];
        if (current.includes(userId)) {
            setValue(
                'assigneeIds',
                current.filter((id) => id !== userId)
            );
        } else {
            setValue('assigneeIds', [...current, userId]);
        }
    };

    // Filter custom fields to only show those that are visible in the current view
    const visibleCustomFields = customFields.filter(field => {
        const columnId = `custom_${field.id}`;
        return visibleColumns.includes(columnId);
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Add Subtask</DialogTitle>
                    <DialogDescription>
                        Create a subtask for "{parentTask?.title}"
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                    {/* Title */}
                    <div className="space-y-2">
                        <Label htmlFor="title">
                            Title <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="title"
                            placeholder="Enter subtask title..."
                            {...register('title')}
                            className={errors.title ? 'border-destructive' : ''}
                            autoFocus
                        />
                        {errors.title && (
                            <p className="text-sm text-destructive">{errors.title.message}</p>
                        )}
                    </div>

                    {/* Status and Priority row - only show if visible */}
                    {(visibleColumns.includes('status') || visibleColumns.includes('priority')) && (
                        <div className="grid grid-cols-2 gap-4">
                            {/* Status */}
                            {visibleColumns.includes('status') && (
                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <Select
                                        value={selectedStatus}
                                        onValueChange={(value) => setValue('status', value as TaskStatus)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(statusConfig).map(([key, config]) => (
                                                <SelectItem key={key} value={key}>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={config.variant} className="text-xs">
                                                            {getStatusLabel(key as TaskStatus, t)}
                                                        </Badge>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Priority */}
                            {visibleColumns.includes('priority') && (
                                <div className="space-y-2">
                                    <Label>Priority</Label>
                                    <Select
                                        value={selectedPriority}
                                        onValueChange={(value) => setValue('priority', value as TaskPriority)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select priority" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(priorityConfig).map(([key, config]) => (
                                                <SelectItem key={key} value={key}>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={config.variant} className="text-xs">
                                                            {getPriorityLabel(key as TaskPriority, t)}
                                                        </Badge>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Due Date - only show if visible */}
                    {visibleColumns.includes('dueDate') && (
                        <div className="space-y-2">
                            <Label htmlFor="dueDate">Due Date</Label>
                            <div className="relative">
                                <Input
                                    id="dueDate"
                                    type="date"
                                    {...register('dueDate')}
                                    className="pl-10"
                                />
                                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                            </div>
                        </div>
                    )}

                    {/* Assignees - only show if visible */}
                    {visibleColumns.includes('assignees') && (
                        <div className="space-y-2">
                            <Label>Assignees</Label>
                            <div className="border border-border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                                {allMembers.map((member) => (
                                    <label
                                        key={member.userId}
                                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-hover cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={selectedAssignees?.includes(member.userId)}
                                            onCheckedChange={() => toggleAssignee(member.userId)}
                                        />
                                        <Avatar className="h-7 w-7">
                                            <AvatarImage src={member.user.avatarUrl || undefined} />
                                            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                                                {getInitials(member.user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground truncate">
                                                {member.user.name}
                                            </p>
                                        </div>
                                        {projectOwnerId && member.userId === projectOwnerId && (
                                            <Badge variant="outline" className="text-xs">
                                                Owner
                                            </Badge>
                                        )}
                                    </label>
                                ))}
                            </div>
                            {/* Selected assignees display */}
                            {selectedAssignees && selectedAssignees.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedAssignees.map((userId) => {
                                        const member = allMembers.find((m) => m.userId === userId);
                                        if (!member) return null;
                                        return (
                                            <Badge
                                                key={userId}
                                                variant="secondary"
                                                className="pl-1 pr-2 py-1"
                                            >
                                                <Avatar className="h-5 w-5 mr-1">
                                                    <AvatarImage src={member.user.avatarUrl || undefined} />
                                                    <AvatarFallback className="text-[10px]">
                                                        {getInitials(member.user.name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                {member.user.name}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleAssignee(userId)}
                                                    className="ml-1 hover:text-destructive"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Custom Fields - only show visible ones */}
                    {visibleCustomFields.length > 0 && (
                        <div className="space-y-4 pt-2 border-t border-border">
                            <Label className="text-sm font-medium">Custom Fields</Label>
                            {visibleCustomFields.map((field) => (
                                <div key={field.id} className="space-y-2">
                                    <Label htmlFor={`custom-${field.id}`} className="text-sm">
                                        {field.name}
                                    </Label>

                                    {/* TEXT field */}
                                    {field.type === 'TEXT' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onChange={(e) =>
                                                setCustomFieldValues((prev) => ({
                                                    ...prev,
                                                    [field.id]: e.target.value,
                                                }))
                                            }
                                            placeholder={`Enter ${field.name.toLowerCase()}...`}
                                        />
                                    )}

                                    {/* NUMBER field */}
                                    {field.type === 'NUMBER' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            type="number"
                                            value={(customFieldValues[field.id] as number) ?? ''}
                                            onChange={(e) =>
                                                setCustomFieldValues((prev) => ({
                                                    ...prev,
                                                    [field.id]: e.target.value ? Number(e.target.value) : null,
                                                }))
                                            }
                                        />
                                    )}

                                    {/* SELECT field */}
                                    {field.type === 'SELECT' && (
                                        <Select
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onValueChange={(value) =>
                                                setCustomFieldValues((prev) => ({
                                                    ...prev,
                                                    [field.id]: value || null,
                                                }))
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={`Select ${field.name.toLowerCase()}...`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {field.options.map((option) => (
                                                    <SelectItem key={option.id} value={option.id}>
                                                        <div className="flex items-center gap-2">
                                                            {option.color && (
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{ backgroundColor: option.color }}
                                                                />
                                                            )}
                                                            {option.label}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    {/* CHECKBOX field */}
                                    {field.type === 'CHECKBOX' && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id={`custom-${field.id}`}
                                                checked={(customFieldValues[field.id] as boolean) || false}
                                                onCheckedChange={(checked) =>
                                                    setCustomFieldValues((prev) => ({
                                                        ...prev,
                                                        [field.id]: !!checked,
                                                    }))
                                                }
                                            />
                                            <Label htmlFor={`custom-${field.id}`} className="text-sm cursor-pointer">
                                                {field.name}
                                            </Label>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Add Subtask
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
